import { useEffect, useMemo, useRef, useState } from "react";
import { collection, getDocs } from "firebase/firestore";
import { FileUp, Lock, Sparkles, X } from "lucide-react";
import {
  MAPPABLE_FIELDS,
  TEAM_FIELDS,
  formatHomeAddress,
  guessColumns,
  hourlyCents,
  isActiveStatus,
  matchDepartment,
  normalizePhone,
  paths,
  personName,
  roleFromTitle,
  tidyName,
  type ColumnGuess,
  type Role,
  type TeamFieldId,
} from "@clevis/shared-types";
import { Button, Card, CardTitle, Select, cn } from "@clevis/shared-ui";
import { db } from "../lib/firebase";
import { call, CallError } from "../lib/callFunction";
import { parseCsv } from "../lib/csv";
import { textareaClass } from "./JobEditCard";

// Import the team from any payroll or HR export: a CSV file, or rows copied
// out of Excel. Clevis reads every column, predicts what each one is (from
// its header and its values) and says why; Clevis AI double-checks using only
// headers, value shapes, titles and department names. Then job titles become
// roles, department names match Clevis departments, and each person is shown
// as invite / update / turn off / skip before anything is saved.

type Assignable = Exclude<Role, "owner">;
type Action = "invite" | "update" | "disable" | "skip";
interface Member {
  uid: string;
  email: string;
  displayName: string;
  role: Role;
  disabled: boolean;
  payrollId: string;
}
interface Dept {
  id: string;
  name: string;
  kind?: "department" | "group";
}
interface Person {
  row: number;
  name: string;
  email: string;
  phone: string;
  title: string;
  deptValue: string;
  rateCents: number | null;
  payrollId: string;
  /** Home address from the export (private, like pay). */
  address: { line1: string; line2: string; city: string; state: string; zip: string } | null;
  active: boolean;
  role: Assignable;
  match: Member | null;
  matchedBy: string;
  action: Action;
  note: string;
}

const ROLE_LABEL: Record<Assignable, string> = { admin: "Admin", manager: "Manager", staff: "Office staff", tech: "Field tech" };
const ACTION_LABEL: Record<Action, string> = { invite: "Invite", update: "Update", disable: "Turn off", skip: "Skip" };
const LABEL = Object.fromEntries(TEAM_FIELDS.map((f) => [f.id, f.label])) as Record<TeamFieldId, string>;
const money = (c: number) => `$${(c / 100).toFixed(2)}`;
const key = (s: string) => s.trim().toLowerCase();
const shape = (v: string) => v.trim().replace(/[A-Za-z]/g, "a").replace(/\d/g, "9").slice(0, 40);
const normName = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();

/** Excel copy-paste is tab separated; files are CSV. Judged on the first lines (a title line may come first). */
function parseTable(text: string): string[][] {
  const head = text.split(/\r?\n/, 8).join("\n");
  const tabs = (head.match(/\t/g) ?? []).length;
  const commas = (head.match(/,/g) ?? []).length;
  if (tabs > 0 && tabs >= commas / 2) return text.split(/\r?\n/).map((l) => l.split("\t")).filter((r) => r.some((c) => c.trim()));
  return parseCsv(text);
}

export function TeamImport({ tenantId, meUid, departments, onClose, onDone }: { tenantId: string; meUid: string; departments: Dept[]; onClose: () => void; onDone: (msg: string) => void }) {
  const [source, setSource] = useState("");
  const [paste, setPaste] = useState("");
  const [table, setTable] = useState<{ headers: string[]; rows: string[][] } | null>(null);
  const [cols, setCols] = useState<ColumnGuess[]>([]);
  const [touchedCols, setTouchedCols] = useState<Set<number>>(new Set());
  const [ai, setAi] = useState<"idle" | "checking" | "done" | "failed">("idle");
  const [titleRoles, setTitleRoles] = useState<Record<string, Assignable>>({});
  const [deptMap, setDeptMap] = useState<Record<string, string>>({}); // value → dept id | "new" | ""
  const touched = useRef<Set<string>>(new Set());
  const [members, setMembers] = useState<Member[]>([]);
  const [invitedEmails, setInvitedEmails] = useState<Set<string>>(new Set());
  const [overrides, setOverrides] = useState<Record<number, Partial<Pick<Person, "action" | "role">>>>({});
  const [emailInvites, setEmailInvites] = useState(false);
  const [updateRoles, setUpdateRoles] = useState(false);
  const [updateRates, setUpdateRates] = useState(true);
  const [turnOffLeavers, setTurnOffLeavers] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState<{ invited: number; updatedInvites: number; updated: number; disabled: number; skipped: number; emailed: number; problems: Array<{ name: string; reason: string }> } | null>(null);

  useEffect(() => {
    void getDocs(collection(db, paths.tenantMembers(tenantId))).then((s) =>
      setMembers(
        s.docs.map((d) => ({
          uid: d.id,
          email: String(d.get("email") ?? "").toLowerCase(),
          displayName: String(d.get("displayName") ?? ""),
          role: (d.get("role") as Role) ?? "staff",
          disabled: d.get("disabled") === true,
          payrollId: String(d.get("payrollId") ?? ""),
        })),
      ),
    );
    void call<{ invites: Array<{ email: string }> }>("listTenantInvites", { tenantId }, { mutation: false })
      .then((r) => setInvitedEmails(new Set(r.invites.map((i) => key(i.email)))))
      .catch(() => undefined);
  }, [tenantId]);

  const colOf = (f: TeamFieldId) => cols.find((c) => c.field === f)?.index ?? -1;
  const cell = (r: string[], f: TeamFieldId) => {
    const i = colOf(f);
    return i >= 0 ? (r[i] ?? "").trim() : "";
  };

  // Distinct titles and department values, with how many people each.
  const titles = useMemo(() => {
    if (!table) return [] as Array<[string, number]>;
    const m = new Map<string, number>();
    for (const r of table.rows) {
      const t = cell(r, "title") || cell(r, "role");
      if (t) m.set(t, (m.get(t) ?? 0) + 1);
    }
    return [...m.entries()].sort((a, b) => b[1] - a[1]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [table, cols]);
  const deptValues = useMemo(() => {
    if (!table) return [] as Array<[string, number]>;
    const m = new Map<string, number>();
    for (const r of table.rows) {
      const v = cell(r, "department");
      if (v) m.set(v, (m.get(v) ?? 0) + 1);
    }
    return [...m.entries()].sort((a, b) => b[1] - a[1]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [table, cols]);

  // Clevis's own guesses for titles and departments (until someone changes one, or the AI weighs in).
  useEffect(() => {
    setTitleRoles((prev) => Object.fromEntries(titles.map(([t]) => [key(t), touched.current.has(`t:${key(t)}`) ? prev[key(t)]! : (prev[key(t)] ?? roleFromTitle(t))])));
    setDeptMap((prev) => Object.fromEntries(deptValues.map(([v]) => [key(v), touched.current.has(`d:${key(v)}`) ? prev[key(v)]! : (prev[key(v)] ?? matchDepartment(v, departments) ?? "new")])));
  }, [titles, deptValues, departments]);

  function read(text: string, name: string) {
    setError("");
    setResult(null);
    const all = parseTable(text);
    if (all.length < 2) return setError("That has no rows under its header row.");
    // Some exports put a title line above the real header: use the first row with 3+ filled cells.
    const start = all.findIndex((r) => r.filter((c) => c.trim()).length >= 3);
    const headers = (all[start] ?? all[0]!).map((h, i) => h.trim() || `Column ${i + 1}`);
    const rows = all.slice(start + 1).filter((r) => r.some((c) => c.trim()));
    if (!rows.length) return setError("That has no rows under its header row.");
    const guesses = guessColumns(headers, rows);
    setTable({ headers, rows });
    setCols(guesses);
    setTouchedCols(new Set());
    touched.current = new Set();
    setOverrides({});
    setSource(name);
    void askAi(headers, rows, guesses);
  }

  async function askAi(headers: string[], rows: string[][], guesses: ColumnGuess[]) {
    setAi("checking");
    const plain = new Set<TeamFieldId>(["title", "department", "role", "status"]);
    const distinct = (i: number) => [...new Set(rows.map((r) => (r[i] ?? "").trim()).filter(Boolean))];
    const titleCol = guesses.find((g) => g.field === "title")?.index ?? -1;
    const deptCol = guesses.find((g) => g.field === "department")?.index ?? -1;
    try {
      const res = await call<{ columns: Array<{ index: number; field: TeamFieldId; why: string }>; titleRoles: Array<{ title: string; role: Assignable }>; departments: Array<{ value: string; departmentId: string | null; create: boolean }> }>(
        "suggestTeamImport",
        {
          tenantId,
          columns: guesses.map((g) => ({
            index: g.index,
            header: headers[g.index]!.slice(0, 120),
            guess: g.field,
            shapes: g.field === "sensitive" ? [] : [...new Set(rows.slice(0, 20).map((r) => shape(r[g.index] ?? "")).filter(Boolean))].slice(0, 5),
            values: plain.has(g.field) ? distinct(g.index).slice(0, 80).map((v) => v.slice(0, 120)) : [],
          })),
          titles: titleCol >= 0 ? distinct(titleCol).slice(0, 150) : [],
          departmentValues: deptCol >= 0 ? distinct(deptCol).slice(0, 100) : [],
        },
        { mutation: false, timeoutMs: 120_000 },
      );
      setCols((cur) =>
        cur.map((g) => {
          const s = res.columns.find((c) => c.index === g.index);
          if (!s || g.field === "sensitive" || touchedColsRef.current.has(g.index)) return g;
          if (s.field === g.field) return { ...g, why: `${g.why} Clevis AI agrees.`, confidence: g.confidence === "low" ? "medium" : g.confidence };
          if (g.confidence === "high") return g;
          return { ...g, field: s.field, why: `Clevis AI: ${s.why}`, confidence: "medium" };
        }),
      );
      setTitleRoles((prev) => {
        const next = { ...prev };
        for (const t of res.titleRoles) if (!touched.current.has(`t:${key(t.title)}`)) next[key(t.title)] = t.role;
        return next;
      });
      setDeptMap((prev) => {
        const next = { ...prev };
        for (const d of res.departments) if (!touched.current.has(`d:${key(d.value)}`)) next[key(d.value)] = d.departmentId ?? (d.create ? "new" : "");
        return next;
      });
      setAi("done");
    } catch {
      setAi("failed");
    }
  }
  const touchedColsRef = useRef(touchedCols);
  touchedColsRef.current = touchedCols;

  // Everyone in the file, as Clevis will treat them.
  const people: Person[] = useMemo(() => {
    if (!table) return [];
    const byPayroll = new Map(members.filter((m) => m.payrollId).map((m) => [key(m.payrollId), m]));
    const byEmail = new Map(members.filter((m) => m.email).map((m) => [m.email, m]));
    const byName = new Map(members.map((m) => [normName(m.displayName), m]));
    return table.rows.map((r, row) => {
      const name = tidyName(personName(cell(r, "fullName"), cell(r, "firstName"), cell(r, "lastName")));
      const email = key(cell(r, "email"));
      const title = cell(r, "title");
      const roleCell = cell(r, "role");
      const payrollId = cell(r, "payrollId");
      const statusCell = cell(r, "status");
      const active = statusCell ? isActiveStatus(statusCell) : true;
      const role: Assignable = roleCell ? roleFromTitle(roleCell) : title ? (titleRoles[key(title)] ?? roleFromTitle(title)) : "staff";
      let match: Member | null = null;
      let matchedBy = "";
      if (payrollId && byPayroll.has(key(payrollId))) [match, matchedBy] = [byPayroll.get(key(payrollId))!, "employee ID"];
      else if (email && byEmail.has(email)) [match, matchedBy] = [byEmail.get(email)!, "email"];
      else if (name && byName.has(normName(name))) [match, matchedBy] = [byName.get(normName(name))!, "name (different email)"];
      const notes: string[] = [];
      let action: Action;
      if (!active) {
        action = match && turnOffLeavers && match.role !== "owner" && match.uid !== meUid && !match.disabled ? "disable" : "skip";
        notes.push(match ? (match.disabled ? "left; already turned off" : "left the company") : "left the company; not on your team");
      } else if (match) {
        action = "update";
        notes.push(`on your team (matched by ${matchedBy})`);
        if (match.disabled) notes.push("turned off in Clevis");
      } else if (email) {
        action = "invite";
        if (invitedEmails.has(email)) notes.push("already invited; the invite is updated");
      } else {
        action = "skip";
        notes.push("no email, so they can't be invited");
      }
      const o = overrides[row] ?? {};
      const st = cell(r, "state");
      const address = { line1: cell(r, "address"), line2: cell(r, "address2"), city: cell(r, "city"), state: st.length === 2 ? st.toUpperCase() : st, zip: cell(r, "zip") };
      return {
        row,
        name: name || email || `Row ${row + 2}`,
        email,
        phone: cell(r, "phone") ? normalizePhone(cell(r, "phone")) : "",
        title,
        deptValue: cell(r, "department"),
        rateCents: hourlyCents(cell(r, "hourlyRate")),
        payrollId,
        address: Object.values(address).some(Boolean) ? address : null,
        active,
        role: o.role ?? role,
        match,
        matchedBy,
        action: o.action ?? action,
        note: notes.join(" · "),
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [table, cols, titleRoles, members, invitedEmails, overrides, turnOffLeavers, meUid]);

  const counts = useMemo(() => people.reduce((m, p) => ({ ...m, [p.action]: (m[p.action] ?? 0) + 1 }), {} as Record<Action, number>), [people]);
  const deptName = (id: string) => departments.find((d) => d.id === id)?.name ?? "";
  const mapped = new Set(cols.map((c) => c.field));
  const hasName = mapped.has("fullName") || mapped.has("firstName") || mapped.has("lastName");

  async function apply() {
    if (!hasName && !mapped.has("email")) return setError("Pick which column has names or emails.");
    setBusy(true);
    setError("");
    const newDepts = deptValues.filter(([v]) => deptMap[key(v)] === "new").map(([v]) => ({ key: key(v).slice(0, 60), name: tidyName(v).slice(0, 80), kind: "department" as const }));
    try {
      const res = await call<NonNullable<typeof result>>(
        "importTeam",
        {
          tenantId,
          source: source.slice(0, 160),
          emailInvites,
          updateRoles,
          updateRates,
          newDepartments: newDepts,
          people: people
            .filter((p) => p.action !== "skip")
            .map((p) => {
              const d = p.deptValue ? deptMap[key(p.deptValue)] : "";
              return {
                email: p.email,
                displayName: p.name === p.email ? "" : p.name,
                phone: p.phone,
                title: p.title,
                payrollId: p.payrollId,
                role: p.role,
                departments: d === "new" ? [`new:${key(p.deptValue).slice(0, 60)}`] : d ? [d] : [],
                hourlyRateCents: p.rateCents,
                homeAddress: p.address,
                action: p.action,
                matchUid: p.match?.uid ?? null,
              };
            }),
        },
        { timeoutMs: 540_000 },
      );
      setResult(res);
      onDone(`Team import: ${res.invited + res.updatedInvites} invited, ${res.updated} updated${res.disabled ? `, ${res.disabled} turned off` : ""}${res.emailed ? `, ${res.emailed} invite emails sent` : ""}.`);
    } catch (err) {
      const e = err as CallError;
      setError(e.hint ? `${e.message} ${e.hint}` : e.message);
    } finally {
      setBusy(false);
    }
  }

  const fieldOptions = MAPPABLE_FIELDS.map((f) => (
    <option key={f} value={f}>
      {LABEL[f]}
    </option>
  ));

  return (
    <Card className="space-y-4">
      <div className="flex items-start justify-between gap-2">
        <div>
          <CardTitle className="mb-0">Import your team</CardTitle>
          <p className="text-xs text-steel">
            From any payroll or HR system (Gusto, ADP, Paychex, QuickBooks…): upload its employee export as a CSV, or copy the rows in Excel and paste them below.
            Nothing is saved until you press Import.
          </p>
        </div>
        <button className="text-steel hover:text-ink" onClick={onClose} aria-label="Close the import">
          <X size={16} />
        </button>
      </div>

      {!table ? (
        <div className="space-y-2">
          <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-lg border border-steel/30 bg-white px-3 py-1.5 text-sm text-ink hover:border-steel">
            <FileUp size={14} /> Upload a CSV file
            <input
              type="file"
              accept=".csv,.tsv,.txt,text/csv"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                e.target.value = "";
                if (f) void f.text().then((t) => read(t, f.name));
              }}
            />
          </label>
          <textarea className={textareaClass} rows={5} value={paste} placeholder="…or paste the rows here, with the header row first" onChange={(e) => setPaste(e.target.value)} />
          <Button disabled={!paste.trim()} onClick={() => read(paste, "pasted rows")}>
            Read it
          </Button>
        </div>
      ) : null}

      {table ? (
        <>
          <section className="space-y-1.5">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h3 className="text-sm font-semibold text-ink">
                1. Columns <span className="font-normal text-steel">· {table.headers.length} read from {source}, {table.rows.length} people</span>
              </h3>
              <span className={cn("inline-flex items-center gap-1 text-xs", ai === "failed" ? "text-steel" : "text-brandorange-dark")}>
                <Sparkles size={12} />
                {ai === "checking" ? "Clevis AI is checking the predictions…" : ai === "done" ? "Checked by Clevis AI" : ai === "failed" ? "AI check unavailable; these are Clevis's own predictions" : ""}
              </span>
            </div>
            <div className="max-h-72 overflow-y-auto rounded-lg border border-steel/15">
              {cols.map((c) => (
                <div key={c.index} className="grid grid-cols-[minmax(0,1fr)_11rem] items-start gap-2 border-b border-steel/10 px-3 py-1.5 text-sm last:border-0 sm:grid-cols-[minmax(0,10rem)_minmax(0,1fr)_11rem]">
                  <span className="truncate font-medium text-ink" title={c.header}>
                    {c.header}
                  </span>
                  <span className="hidden min-w-0 sm:block">
                    <span className="block truncate text-xs text-steel">{c.field === "sensitive" ? "values hidden" : c.samples.join(" · ") || "(empty)"}</span>
                    <span className={cn("block text-[11px]", c.confidence === "low" ? "text-brandorange-dark" : "text-steel/80")}>{c.why}</span>
                  </span>
                  {c.field === "sensitive" ? (
                    <span className="inline-flex items-center gap-1 text-xs text-steel">
                      <Lock size={12} /> Private: never imported
                    </span>
                  ) : (
                    <Select
                      className={cn("w-44 py-1 text-xs", c.confidence === "low" && c.field !== "ignore" && "border-brandorange/60")}
                      value={c.field}
                      aria-label={`What ${c.header} is`}
                      onChange={(e) => {
                        const f = e.target.value as TeamFieldId;
                        setTouchedCols((t) => new Set(t).add(c.index));
                        // A field lives in one column: taking it moves it off another.
                        setCols((cur) => cur.map((x) => (x.index === c.index ? { ...x, field: f, confidence: "high", why: "Set by you." } : x.field === f && f !== "ignore" ? { ...x, field: "ignore", why: `Moved to ${c.header}.` } : x)));
                      }}
                    >
                      {fieldOptions}
                    </Select>
                  )}
                </div>
              ))}
            </div>
          </section>

          {titles.length ? (
            <section className="space-y-1.5">
              <h3 className="text-sm font-semibold text-ink">2. Job titles → Clevis roles</h3>
              <div className="grid gap-x-4 gap-y-1 sm:grid-cols-2">
                {titles.map(([t, n]) => (
                  <div key={t} className="flex items-center gap-2 text-sm">
                    <span className="min-w-0 flex-1 truncate text-ink">
                      {t} <span className="text-xs text-steel">×{n}</span>
                    </span>
                    <Select
                      className="w-36 py-0.5 text-xs"
                      value={titleRoles[key(t)] ?? "staff"}
                      aria-label={`Role for ${t}`}
                      onChange={(e) => {
                        touched.current.add(`t:${key(t)}`);
                        setTitleRoles((m) => ({ ...m, [key(t)]: e.target.value as Assignable }));
                      }}
                    >
                      {(Object.keys(ROLE_LABEL) as Assignable[]).map((r) => (
                        <option key={r} value={r}>
                          {ROLE_LABEL[r]}
                        </option>
                      ))}
                    </Select>
                  </div>
                ))}
              </div>
            </section>
          ) : null}

          {deptValues.length ? (
            <section className="space-y-1.5">
              <h3 className="text-sm font-semibold text-ink">3. Departments</h3>
              <div className="grid gap-x-4 gap-y-1 sm:grid-cols-2">
                {deptValues.map(([v, n]) => (
                  <div key={v} className="flex items-center gap-2 text-sm">
                    <span className="min-w-0 flex-1 truncate text-ink">
                      {v} <span className="text-xs text-steel">×{n}</span>
                    </span>
                    <Select
                      className="w-48 py-0.5 text-xs"
                      value={deptMap[key(v)] ?? ""}
                      aria-label={`Clevis department for ${v}`}
                      onChange={(e) => {
                        touched.current.add(`d:${key(v)}`);
                        setDeptMap((m) => ({ ...m, [key(v)]: e.target.value }));
                      }}
                    >
                      <option value="new">Create “{tidyName(v)}”</option>
                      <option value="">Leave out</option>
                      {departments.map((d) => (
                        <option key={d.id} value={d.id}>
                          {d.name}
                          {d.kind === "group" ? " (group)" : ""}
                        </option>
                      ))}
                    </Select>
                  </div>
                ))}
              </div>
            </section>
          ) : null}

          <section className="space-y-1.5">
            <h3 className="text-sm font-semibold text-ink">
              {titles.length || deptValues.length ? "4" : "2"}. People{" "}
              <span className="font-normal text-steel">
                · {counts.invite ?? 0} to invite · {counts.update ?? 0} to update{counts.disable ? ` · ${counts.disable} to turn off` : ""} · {counts.skip ?? 0} skipped
              </span>
            </h3>
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-ink">
              <label className="flex items-center gap-1.5">
                <input type="checkbox" checked={updateRates} onChange={(e) => setUpdateRates(e.target.checked)} /> Update pay rates for people already on the team
              </label>
              <label className="flex items-center gap-1.5">
                <input type="checkbox" checked={updateRoles} onChange={(e) => setUpdateRoles(e.target.checked)} /> Change roles of people already on the team
              </label>
              <label className="flex items-center gap-1.5">
                <input type="checkbox" checked={turnOffLeavers} onChange={(e) => setTurnOffLeavers(e.target.checked)} /> Turn off people the file says have left
              </label>
              <label className="flex items-center gap-1.5">
                <input type="checkbox" checked={emailInvites} onChange={(e) => setEmailInvites(e.target.checked)} /> Email the invites now
              </label>
            </div>
            <div className="max-h-96 overflow-auto rounded-lg border border-steel/15">
              <table className="w-full min-w-[720px] text-left text-xs">
                <thead className="sticky top-0 bg-cream text-[11px] uppercase tracking-wide text-steel">
                  <tr>
                    <th className="px-2 py-1.5 font-semibold">Person</th>
                    <th className="px-2 py-1.5 font-semibold">Title · department</th>
                    <th className="px-2 py-1.5 font-semibold">Role</th>
                    <th className="px-2 py-1.5 text-right font-semibold">Rate</th>
                    <th className="px-2 py-1.5 font-semibold">What happens</th>
                  </tr>
                </thead>
                <tbody>
                  {people.map((p) => {
                    const d = p.deptValue ? deptMap[key(p.deptValue)] : "";
                    return (
                      <tr key={p.row} className={cn("border-t border-steel/10 align-top", p.action === "skip" && "text-steel")}>
                        <td className="px-2 py-1">
                          <div className={cn("font-medium", p.action === "skip" ? "text-steel" : "text-ink")}>{p.name}</div>
                          <div className="text-[11px] text-steel">{[p.email, p.phone].filter(Boolean).join(" · ") || "no email"}</div>
                          {p.address ? <div className="text-[11px] text-steel">{formatHomeAddress(p.address)}</div> : null}
                        </td>
                        <td className="px-2 py-1">
                          <div>{p.title || "—"}</div>
                          <div className="text-[11px] text-steel">{d === "new" ? `${tidyName(p.deptValue)} (new)` : d ? deptName(d) : p.deptValue ? `${p.deptValue} (left out)` : ""}</div>
                        </td>
                        <td className="px-2 py-1">
                          <Select className="w-32 py-0.5 text-xs" value={p.role} aria-label={`Role for ${p.name}`} disabled={p.match?.role === "owner"} onChange={(e) => setOverrides((o) => ({ ...o, [p.row]: { ...o[p.row], role: e.target.value as Assignable } }))}>
                            {(Object.keys(ROLE_LABEL) as Assignable[]).map((r) => (
                              <option key={r} value={r}>
                                {ROLE_LABEL[r]}
                              </option>
                            ))}
                          </Select>
                        </td>
                        <td className="px-2 py-1 text-right tabular-nums">{p.rateCents !== null ? `${money(p.rateCents)}/hr` : "—"}</td>
                        <td className="px-2 py-1">
                          <Select className="w-28 py-0.5 text-xs" value={p.action} aria-label={`What happens to ${p.name}`} onChange={(e) => setOverrides((o) => ({ ...o, [p.row]: { ...o[p.row], action: e.target.value as Action } }))}>
                            {(Object.keys(ACTION_LABEL) as Action[])
                              .filter((a) => (a === "invite" ? !!p.email && !p.match : a === "update" || a === "disable" ? !!p.match && (a !== "disable" || (p.match.role !== "owner" && p.match.uid !== meUid)) : true))
                              .map((a) => (
                                <option key={a} value={a}>
                                  {ACTION_LABEL[a]}
                                </option>
                              ))}
                          </Select>
                          {p.note ? <div className={cn("mt-0.5 text-[11px]", /different email|no email/.test(p.note) ? "text-brandorange-dark" : "text-steel")}>{p.note}</div> : null}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <p className="text-[11px] text-steel">
              Invites wait under “Waiting to join”{emailInvites ? " and are emailed now" : "; send them from there when you're ready"}. Each person's name, phone, title, department and rate come along when they accept.
            </p>
          </section>

          {error ? <p className="text-xs text-danger">{error}</p> : null}
          {result ? (
            <div className="rounded-lg bg-success/10 px-3 py-2 text-xs text-ink">
              Done: {result.invited} invited{result.updatedInvites ? ` (+${result.updatedInvites} invites updated)` : ""}, {result.updated} updated
              {result.disabled ? `, ${result.disabled} turned off` : ""}
              {result.emailed ? `, ${result.emailed} emails sent` : ""}.
              {result.problems.length ? (
                <ul className="mt-1 list-disc pl-4 text-danger">
                  {result.problems.map((p, i) => (
                    <li key={i}>
                      {p.name}: {p.reason}
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          ) : null}
          <div className="flex flex-wrap gap-2">
            <Button busy={busy} disabled={!!result || people.every((p) => p.action === "skip")} onClick={() => void apply()}>
              Import {people.filter((p) => p.action !== "skip").length} people
            </Button>
            <Button variant="ghost" onClick={() => setTable(null)}>
              Start over
            </Button>
          </div>
        </>
      ) : null}
      {!table && error ? <p className="text-xs text-danger">{error}</p> : null}
    </Card>
  );
}
